import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DefaultDropdownItem } from '..';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { t } from '../../../../types/translation/Translator';
import { PromiseOr } from '../../../../util/types.util';
import DropdownItem from './DropdownItem';
import { VariableSizeList } from 'react-window';

const ESTIMATED_ITEM_HEIGHT = 40;
const MAX_HEIGHT = 200;
const DROPDOWN_VERTICAL_PADDING = 16;
const LIST_MAX_HEIGHT = MAX_HEIGHT - DROPDOWN_VERTICAL_PADDING;

interface DropdownListProps<T> {
  items: T[];
  textBuilder: (item: T) => string;
  itemBuilder?: (item: T, selected: boolean) => React.ReactNode;
  onClick?: (item: T) => PromiseOr<void>;
  firstBuilder?: (selected: boolean) => React.ReactNode;
  lastBuilder?: (selected: boolean) => React.ReactNode;
}

export default function DropdownList<T>({
  items,
  itemBuilder,
  textBuilder,
  onClick,
  firstBuilder,
  lastBuilder,
}: DropdownListProps<T>) {
  const listRef = useRef<VariableSizeList>(null);
  const sizeMap = useRef<Map<number, number>>(new Map());

  const [sizeVersion, setSizeVersion] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [inputMode, setInputMode] = useState<'mouse' | 'keyboard'>('mouse');

  const hasFirst = useMemo(() => firstBuilder && firstBuilder(false), [firstBuilder]);
  const hasLast = useMemo(() => lastBuilder && lastBuilder(false), [lastBuilder]);

  const itemCount = useMemo(() => items.length + (hasFirst ? 1 : 0) + (hasLast ? 1 : 0), [items, hasFirst, hasLast]);
  const maxIndex = useMemo(() => itemCount - 1, [itemCount]);

  const listHeight = useMemo(() => {
    const knownHeight = [...sizeMap.current.values()].reduce((a, b) => a + b, 0);
    const knownCount = sizeMap.current.size;
    const estimatedHeight = Math.max(0, (itemCount - knownCount) * ESTIMATED_ITEM_HEIGHT);

    return Math.min(LIST_MAX_HEIGHT, knownHeight + estimatedHeight);
  }, [sizeVersion, itemCount]);

  useEffect(() => {
    if (selectedIndex > maxIndex) setSelectedIndex(maxIndex);
  }, [maxIndex]);

  useEffect(() => {
    sizeMap.current.clear();
    setSizeVersion(v => v + 1);
    if (listRef.current) listRef.current.resetAfterIndex(0, true);
  }, [items, hasFirst, hasLast]);

  useEffect(() => {
    if (!listRef.current) return;
    if (inputMode === 'mouse') return;

    listRef.current.scrollToItem(selectedIndex, 'smart');
  }, [selectedIndex, itemCount]);

  useEffect(() => {
    const ac = new AbortController();

    document.addEventListener(
      'keydown',
      event => {
        if (event.key === 'ArrowUp') {
          handleIndexDecrease();
          event.preventDefault();
          setInputMode('keyboard');
        } else if (event.key === 'ArrowDown') {
          handleIndexIncrease();
          event.preventDefault();
          setInputMode('keyboard');
        }
      },
      { signal: ac.signal },
    );
    return () => ac.abort();
  }, [maxIndex]);

  const handleIndexDecrease = () => setSelectedIndex(index => (index === 0 ? maxIndex : index - 1));
  const handleIndexIncrease = () => setSelectedIndex(index => (index === maxIndex ? 0 : index + 1));

  const getItemSize = (index: number) => sizeMap.current.get(index) ?? ESTIMATED_ITEM_HEIGHT;

  const Item = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const selected = selectedIndex === index;

    // Calculate and set actual height of element (this is for elements that take up an unknown height)
    const setSizeRef = (element: HTMLDivElement | null) => {
      if (!element) return;
      const height = element.getBoundingClientRect().height;
      const cached = sizeMap.current.get(index) ?? ESTIMATED_ITEM_HEIGHT;
      if (height !== cached) {
        sizeMap.current.set(index, height);
        // Recalculate layout from this item onwards
        if (listRef.current) listRef.current.resetAfterIndex(index, true);
        setSizeVersion(v => v + 1);
      }
    };

    if (hasFirst && index === 0) {
      return (
        <div style={style} onMouseEnter={() => inputMode === 'mouse' && setSelectedIndex(index)}>
          <div ref={setSizeRef}>{firstBuilder && firstBuilder(selected)}</div>
        </div>
      );
    }

    if (hasLast && index === maxIndex) {
      return (
        <div style={style} onMouseEnter={() => inputMode === 'mouse' && setSelectedIndex(index)}>
          <div ref={setSizeRef}>{lastBuilder && lastBuilder(selected)}</div>
        </div>
      );
    }

    const item = items[hasFirst ? index - 1 : index];
    return (
      <div style={style} onMouseEnter={() => inputMode === 'mouse' && setSelectedIndex(index)}>
        <div ref={setSizeRef}>
          <DropdownItem onClick={() => onClick && onClick(item)} selected={selected}>
            {itemBuilder ? itemBuilder(item, selected) : <DefaultDropdownItem text={textBuilder(item)} />}
          </DropdownItem>
        </div>
      </div>
    );
  };

  if (!itemCount) {
    return (
      <div className='max-h-[200px]'>
        <DropdownItem>
          <DefaultDropdownItem text={t(TranslationKey.noItemsFound).singular.upper} />
        </DropdownItem>
      </div>
    );
  }

  return (
    <div className='w-full' onMouseMove={() => inputMode !== 'mouse' && setInputMode('mouse')}>
      <VariableSizeList
        ref={listRef}
        height={listHeight}
        itemCount={itemCount}
        itemSize={getItemSize}
        estimatedItemSize={ESTIMATED_ITEM_HEIGHT}
        width={'100%'}
      >
        {Item}
      </VariableSizeList>
    </div>
  );
}
